import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { VatPercentages } from 'src/app/shared/constants';

import Utils from 'src/app/shared/utils';

import {
  ApplicabilityType,
  Barn,
  Discount,
  DiscountLabelEnum,
  NweDiscountLabelEnum,
} from '../../models/cycle.model';
import { FinalSetupNavigationService } from '../../services/final-setup-navigation.service';
import { setSelectedDiscountId } from '../../store/actions/cycle.actions';
import { getBarnsInUse } from '../../store/selectors/day-old-chick.selectors';
import { getSelectedDiscount } from '../../store/selectors/discount.selectors';

export interface DiscountItemDialogInputData extends AbstractFormDialogData {
  discountId?: number;
}
export interface DiscountItemDialogResultData {
  discount: Discount;
}
@Component({
  selector: 'app-discount-item-dialog',
  templateUrl: './discount-item-dialog.component.html',
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatInput,
    AgrNumberDirective,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DiscountItemDialogComponent extends AbstractDialog<DiscountItemDialogResultData> {
  data = inject<DiscountItemDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        DiscountItemDialogComponent,
        AbstractDialogResultData<DiscountItemDialogResultData>
      >
    >(MatDialogRef);
  private store = inject<Store<RootState>>(Store);
  private localeService = inject(LocaleService);
  private finalSetupNavigationService = inject(FinalSetupNavigationService);

  id: number;

  formGroup = new FormGroup({
    barn: new FormControl<Barn>(undefined, [Validators.required]),
    applicabilityType: new FormControl<ApplicabilityType>(undefined, Validators.required),
    label: new FormControl<DiscountLabelEnum | NweDiscountLabelEnum>(
      undefined,
      Validators.required,
    ),
    amount: new FormControl<number>(undefined, Validators.required),
    invoiceDate: new FormControl<string>(null, []),
    invoiceNumber: new FormControl<string>(null, [Validators.maxLength(50)]),
    vatPercentage: new FormControl<number>(null, []),
    description: new FormControl<string>(null, [Validators.maxLength(200)]),
    showInFinalSetup: new FormControl<boolean>(null, []),
  });

  barns$ = this.store.select(getBarnsInUse);
  location$ = this.store.select(UserSelectors.selectLocation);
  isFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();
  vatPercentages = VatPercentages;
  isFinalSetup: boolean;
  discountLabelEnum = NweDiscountLabelEnum;

  applicabilityTypes: ApplicabilityType[] = Object.values(ApplicabilityType);
  arrivalCostLabels: DiscountLabelEnum[] | NweDiscountLabelEnum[];
  departureRevenueLabels: DiscountLabelEnum[] | NweDiscountLabelEnum[];
  feedCostLabels: DiscountLabelEnum[] | NweDiscountLabelEnum[];
  labels: string[];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.id = this.data.discountId;

    this.location$
      .pipe(take(1))
      .subscribe((location) => this.initLabelsByCountry(location.country));

    if (this.id > 0) {
      this.data.title = this.localeService.translate('discount-title');

      this.store.dispatch(setSelectedDiscountId({ id: this.id }));
      this.store
        .select(getSelectedDiscount)
        .pipe(
          withLatestFrom(this.barns$),
          skipWhile(
            ([discount, barns]) =>
              discount === null || discount === undefined || barns?.length === 0,
          ),
          take(1),
        )
        .subscribe(([discount, barns]) => {
          this.setLabels(discount.applicabilityType);
          this.formGroup.controls.barn.setValue(barns.find((b) => b.id === discount.barn?.id));
          this.formGroup.controls.applicabilityType.setValue(discount.applicabilityType);
          this.formGroup.controls.label.setValue(discount.label);
          this.formGroup.controls.amount.setValue(discount.amount);
          this.formGroup.controls.invoiceDate.setValue(discount.invoiceDate);
          this.formGroup.controls.invoiceNumber.setValue(discount.invoiceNumber);
          this.formGroup.controls.vatPercentage.setValue(discount.vatPercentage);
          this.formGroup.controls.description.setValue(discount.description);
          this.formGroup.controls.showInFinalSetup.setValue(discount.showInFinalSetup);
        });
    } else {
      this.data.title = this.localeService.translate('discount-item.add-discount');
    }

    this.isFinalSetup$
      .pipe(take(1))
      .subscribe((isFinalSetup) => (this.isFinalSetup = isFinalSetup));
  }

  initLabelsByCountry(country: string) {
    switch (country) {
      case 'NL':
      case 'BE':
      case 'DE':
        this.arrivalCostLabels = [
          NweDiscountLabelEnum.IntegrationPremium,
          NweDiscountLabelEnum.NewConstructionPremium,
          NweDiscountLabelEnum.NetPresentValueDistribution,
          NweDiscountLabelEnum.Discount,
          NweDiscountLabelEnum.Other,
        ];
        this.departureRevenueLabels = [
          NweDiscountLabelEnum.IntegrationPremium,
          NweDiscountLabelEnum.NewConstructionPremium,
          NweDiscountLabelEnum.NetPresentValueDistribution,
          NweDiscountLabelEnum.BackPayment,
          NweDiscountLabelEnum.Other,
        ];
        this.feedCostLabels = [
          NweDiscountLabelEnum.IntegrationPremium,
          NweDiscountLabelEnum.MemberBenefit,
          NweDiscountLabelEnum.MemberDiscount,
          NweDiscountLabelEnum.NewConstructionPremium,
          NweDiscountLabelEnum.NetPresentValueDistribution,
          NweDiscountLabelEnum.CorrectionFeedPrice,
          NweDiscountLabelEnum.CompensationPerAgreement,
          NweDiscountLabelEnum.Other,
        ];
        break;

      default:
        break;
    }
  }

  applicabilityTypeChanged($event: MatSelectChange) {
    this.formGroup.controls.label.setValue(null);
    this.setLabels($event.value);
  }

  private setLabels(applicabilityType: ApplicabilityType) {
    switch (applicabilityType) {
      case ApplicabilityType.ArrivalCost:
        this.labels = this.arrivalCostLabels;
        break;

      case ApplicabilityType.DepartureRevenue:
        this.labels = this.departureRevenueLabels;
        break;

      case ApplicabilityType.FeedCost:
        this.labels = this.feedCostLabels;
        break;

      default:
        break;
    }
  }

  save() {
    if (this.formGroup.valid) {
      const discount: Discount = {
        id: this.id,
        barn: this.formGroup.controls.barn.value,
        applicabilityType: this.formGroup.controls.applicabilityType.value,
        label: this.formGroup.controls.label.value,
        amount: this.formGroup.controls.amount.value,
        invoiceDate: Utils.parseDateTimeFromFormValue(this.formGroup.controls.invoiceDate.value),
        invoiceNumber: this.formGroup.controls.invoiceNumber.value,
        vatPercentage: this.formGroup.controls.vatPercentage.value,
        description: this.formGroup.controls.description.value,
        showInFinalSetup: this.formGroup.controls.showInFinalSetup.value,
      };
      this.confirm({
        discount,
      });
    }
  }
}
